import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import SiteFooter from "@/components/SiteFooter";
import HeroDropZone from "@/components/HeroDropZone";
import {
  ArrowRight, Sparkles, Layers, Users2, Zap, ShieldCheck,
  Check, Star, Quote, AlertTriangle, Clock, FileWarning, Wrench,
} from "lucide-react";

// iter-61 — Direct-response landing page per operator brief. Uses the
// PASPO framework: Problem → Agitation → Solution → Proof → Offer.

const AGITATIONS = [
  { icon: Clock,       title: "Weeks lost to the final mile",
    body: "You wrote the book. Now you're wrestling margins and hidden page-break bugs instead of shipping." },
  { icon: FileWarning, title: "Broken exports across devices",
    body: "Orphan headings on iOS. List markers gone rogue on Kindle. Callouts collapsing mid-page in Safari." },
  { icon: Wrench,      title: "The $500 typographer tax",
    body: "Outsource it and wait a week. Do it yourself and burn a weekend. Neither should be the choice." },
  { icon: AlertTriangle, title: "Rigid template lock-in",
    body: "Every layout tool forces one grid on every book. Your voice deserves better than a preset." },
];

const PILLARS = [
  { icon: Layers, title: "Intelligent Structure Classification",
    body: "Our parser doesn't just read words — it recognises intent. 25+ semantic roles map every paragraph: part labels, chapters, callouts, blockquotes, bullets, section titles. Never a mis-classified line." },
  { icon: Zap, title: "Unified Multi-Format Export",
    body: "One source file → print-ready PDF (WeasyPrint, leader-dot TOC), EPUB 3.0 with embedded fonts, and cleaned developer-ready DOCX. All three in a single click, all three spec-compliant." },
  { icon: Sparkles, title: "Automated Content Optimization",
    body: "Auto-promotes unstructured line runs into crisp bulleted lists. Enforces strict widow/orphan protection. Wraps RN Insight, Bedside Example and custom callouts into styled containers." },
  { icon: Users2, title: "AI-Powered Collaborative Workspace",
    body: "Claude proposes chapter splits. Gemini Nano Banana generates hero art. Editors join with live cursors, thread-level comments, and per-book locks so no one saves over anyone." },
];

const METRICS = [
  { value: "768+", label: "Bullets auto-promoted",
    body: "Verified live across a 22-chapter medical manuscript. Zero layout crashes." },
  { value: "40/40", label: "Core paths green",
    body: "Every build runs comprehensive QA — TOC alignment, chapter boundaries, file integrity." },
  { value: "25+", label: "Semantic block roles",
    body: "From part labels to reflection questions, every line lands where the reader expects it." },
];

const TIERS = [
  {
    name: "Free Trial",
    price: "$0",
    cadence: "Forever",
    tag: "Start here",
    cta: "Get Started Free",
    to: "/register",
    highlighted: false,
    testid: "pricing-tier-free",
    features: [
      "1 Active Book Project",
      "Standard Layout Templates",
      "Web-Only Read Previews",
      "Basic Section Slot Editing",
    ],
  },
  {
    name: "Pro Author",
    price: "$19",
    cadence: "per month",
    tag: "Most popular",
    cta: "Access Pro Toolkit",
    to: "/register",
    highlighted: true,
    testid: "pricing-tier-pro",
    features: [
      "Unlimited Active Book Layouts",
      "Full Access to all Style Packs",
      "Unrestricted PDF, EPUB, DOCX Exports",
      "AI Chapter Split & Cover Art Engines",
    ],
  },
  {
    name: "Publisher Workspace",
    price: "$49",
    cadence: "per month",
    tag: "For teams",
    cta: "Scale Your Team",
    to: "/workspaces",
    highlighted: false,
    testid: "pricing-tier-publisher",
    features: [
      "Multi-User Shared Workspace",
      "Multiple Admin Permission Roles",
      "Real-Time Workspace Presence & Alerts",
      "Live Collaborative Chat & Comments",
    ],
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <Nav />

      {/* ─── Masthead ─────────────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-6 pt-8 pb-2">
        <div className="masthead-rule flex items-center justify-between text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
          <span>Vol. I · Issue 1</span>
          <span className="hidden sm:inline">
            The Independent Publisher's Companion
          </span>
          <span>Est. 2026</span>
        </div>
      </section>

      {/* ─── HERO — PASPO §1: Above-the-fold value promise ───────────── */}
      <section className="max-w-[1400px] mx-auto px-6 pt-12 pb-20 grain" data-testid="hero-section">
        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)] mb-4">
              An AI-powered typesetting studio
            </p>
            <h1
              className="font-editorial font-light tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-[6.5rem]"
              data-testid="hero-headline"
            >
              From manuscript to bookshelf
              <span className="block italic text-[color:var(--primary)]">
                in one single click.
              </span>
            </h1>
            <p
              className="mt-8 max-w-2xl text-lg leading-relaxed text-[color:var(--text-muted)]"
              data-testid="hero-subheadline"
            >
              Stop battling rigid templates, broken text runs, and manual margins.
              Drop your raw draft into BookForge360 and watch our intelligent
              semantic engine compile print-ready PDFs, beautiful EPUBs, and
              clean DOCX files in seconds.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 text-xs text-[color:var(--text-muted)]">
              <span className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-[color:var(--primary)]" strokeWidth={2} />
                No credit card
              </span>
              <span className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-[color:var(--primary)]" strokeWidth={2} />
                First 3 chapters free
              </span>
              <span className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-[color:var(--primary)]" strokeWidth={2} />
                Export in ~60 seconds
              </span>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <HeroDropZone />
          </div>
        </div>
      </section>

      {/* ─── AGITATION — PASPO §2: The formatting tax ──────────────────── */}
      <section
        className="border-t border-[color:var(--border)] bg-white"
        data-testid="agitation-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-12 lg:col-span-5">
              <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
                § 02 — The problem
              </p>
              <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
                The invisible formatting tax that
                <em className="text-[color:var(--primary)] block mt-1">delays book launches.</em>
              </h2>
              <p className="mt-6 text-[color:var(--text-muted)] leading-relaxed">
                Independent authors routinely lose weeks to the final mile of
                self-publishing. You write a masterpiece — only to spend days
                wrestling shifting margins, hidden page-break bugs inside
                Safari, orphan headings, and chaotic list markers that render
                incorrectly across devices.
              </p>
              <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed">
                You shouldn't have to choose between spending $500 on an
                outsourced human typographer or fighting rigid, outdated
                software. BookForge360 acts as your private typesetting
                studio, recognising the deep structure of your book
                automatically so you can focus entirely on your craft.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[color:var(--border)] border border-[color:var(--border)]">
                {AGITATIONS.map((a) => (
                  <div
                    key={a.title}
                    className="bg-white p-7"
                    data-testid={`agitation-card-${a.title.toLowerCase().replace(/\s+/g, "-").slice(0, 20)}`}
                  >
                    <a.icon
                      className="w-5 h-5 mb-4 text-[color:var(--primary)]"
                      strokeWidth={1.5}
                    />
                    <h3 className="font-editorial text-2xl mb-2 leading-tight">
                      {a.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-[color:var(--text-muted)]">
                      {a.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SOLUTION — PASPO §3: Feature pillars ──────────────────────── */}
      <section
        className="border-t border-[color:var(--border)]"
        data-testid="solution-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § 03 — The engine
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
              Built on the <em className="text-[color:var(--primary)]">formatter_v2</em>{" "}
              deep semantic pipeline.
            </h2>
            <p className="mt-6 text-lg text-[color:var(--text-muted)] leading-relaxed">
              Not another template. A parser that understands your book at
              the paragraph level and lays it out like a trade publisher would.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {PILLARS.map((p, idx) => (
              <div
                key={p.title}
                className="bg-white border border-[color:var(--border)] p-8 transition-all duration-300 hover:border-[color:var(--text)]"
                data-testid={`pillar-card-${idx}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#f2f1ec] flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-editorial text-2xl leading-tight mb-3">
                      {p.title}
                    </h3>
                    <p className="text-[color:var(--text-muted)] leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 link-underline text-sm uppercase tracking-[0.15em]"
              data-testid="cta-features-deepdive"
            >
              Explore the full engine <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── PROOF — PASPO §4: Metrics + testimonial ───────────────────── */}
      <section
        className="border-t border-[color:var(--border)] bg-[#f4f2ec]"
        data-testid="proof-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § 04 — Proof
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
              Proven over millions of{" "}
              <em className="text-[color:var(--primary)]">compiled paragraphs.</em>
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--border)] border border-[color:var(--border)]">
            {METRICS.map((m, idx) => (
              <div
                key={m.value}
                className="bg-[#faf9f6] p-8"
                data-testid={`metric-card-${idx}`}
              >
                <div className="font-editorial text-6xl leading-none text-[color:var(--primary)]">
                  {m.value}
                </div>
                <div className="mt-3 text-sm uppercase tracking-[0.15em]">
                  {m.label}
                </div>
                <p className="mt-3 text-sm text-[color:var(--text-muted)] leading-relaxed">
                  {m.body}
                </p>
              </div>
            ))}
          </div>

          <figure
            className="mt-14 max-w-4xl mx-auto text-center"
            data-testid="testimonial"
          >
            <Quote
              className="w-10 h-10 mx-auto mb-6 text-[color:var(--primary)]"
              strokeWidth={1.25}
            />
            <blockquote className="font-editorial text-2xl sm:text-3xl leading-snug italic">
              "I was preparing a comprehensive 22-chapter medical book. Past
              software formats broke my nested callouts and turned my resource
              references into chaotic text soup. BookForge360 parsed the entire
              draft flawlessly in a single pass, giving me beautiful print-ready
              files instantly."
            </blockquote>
            <figcaption className="mt-6 text-sm uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
              Richard Lee Garcia, RN, BSN
              <div className="normal-case tracking-normal mt-1 text-[color:var(--text-muted)]">
                Author of <em>Your Nervous System Is Stuck</em>
              </div>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ─── OFFER — PASPO §5: Pricing tiers ───────────────────────────── */}
      <section
        className="border-t border-[color:var(--border)]"
        id="pricing"
        data-testid="pricing-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § 05 — The offer
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
              Transparent pricing for{" "}
              <em className="text-[color:var(--primary)]">ambitious writers.</em>
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {TIERS.map((t) => (
              <div
                key={t.name}
                className={`relative rounded-lg p-8 flex flex-col transition-all duration-300 ${
                  t.highlighted
                    ? "bg-white border-2 border-[color:var(--primary)] shadow-[0_20px_40px_-20px_rgba(217,93,57,0.25)] lg:-translate-y-4 lg:scale-[1.02]"
                    : "bg-white border border-[color:var(--border)] hover:border-[color:var(--text)]"
                }`}
                data-testid={t.testid}
              >
                {t.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[color:var(--primary)] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full inline-flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" strokeWidth={0} />
                    {t.tag}
                  </span>
                )}
                {!t.highlighted && (
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
                    {t.tag}
                  </span>
                )}
                <h3
                  className={`font-editorial mt-4 ${
                    t.highlighted ? "text-3xl" : "text-2xl"
                  }`}
                >
                  {t.name}
                </h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span
                    className={`font-editorial ${
                      t.highlighted ? "text-6xl" : "text-5xl"
                    } text-[color:var(--text)]`}
                  >
                    {t.price}
                  </span>
                  <span className="text-sm text-[color:var(--text-muted)]">
                    {t.cadence}
                  </span>
                </div>
                <ul className="mt-6 space-y-3 text-sm flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check
                        className={`w-4 h-4 mt-0.5 shrink-0 ${
                          t.highlighted
                            ? "text-[color:var(--primary)]"
                            : "text-[color:var(--text)]"
                        }`}
                        strokeWidth={2}
                      />
                      <span className="text-[color:var(--text-muted)]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={t.to}
                  className={`mt-8 text-center py-3 px-6 rounded transition-all duration-200 inline-flex items-center justify-center gap-2 ${
                    t.highlighted
                      ? "btn-primary"
                      : "btn-ghost hover:bg-[color:var(--text)] hover:text-white"
                  }`}
                  data-testid={`${t.testid}-cta`}
                >
                  {t.cta} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)] inline-flex items-center gap-2 justify-center w-full">
            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
            30-day money-back guarantee · Cancel anytime · No lock-in
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
